import { cn } from '#src/styling/cn.ts';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';

type ButtonTagProps = ComponentProps<'button'>;
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'underline' | 'link';
type ButtonTone = 'destructive';
type ButtonSize = 'md' | 'sm' | 'xs' | 'icon' | 'icon-sm' | 'icon-lg' | 'none';

export type BaseButtonRootProps = ButtonTagProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  tone?: ButtonTone;
  loading?: boolean;
  asChild?: boolean;
};

const rootVariants = cva(
  "focus-visible:ring-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive group inline-block cursor-pointer whitespace-nowrap transition-all outline-none focus-visible:ring-3 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // mt-1.5 is there to compensate for -translate-y-1.5 on any 3d variant (primary, secondary, destructive)
        primary: 'bg-primary-shade mt-1.5',
        secondary: 'bg-primary/85 mt-1.5',
        ghost: 'hover:text-accent-foreground hover:bg-gray-100',
        destructive: 'bg-destructive-shade mt-1.5',
        underline: 'text-muted-foreground hover:text-foreground underline underline-offset-3',
        link: 'underline-offset-2 hover:underline',
      },
      tone: {
        destructive: '',
      },
      size: {
        md: 'h-10 rounded-md',
        sm: 'h-8 rounded-sm',
        xs: 'h-8 rounded-sm',
        icon: 'size-10 rounded',
        'icon-sm': 'size-8 rounded',
        'icon-lg': 'size-12 rounded-sm',
        none: 'h-auto p-0',
      },
    },
    compoundVariants: [{ variant: 'secondary', tone: 'destructive', class: 'bg-destructive/85' }],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

const BaseButtonRoot = ({
  className,
  variant,
  size,
  tone,
  loading,
  asChild,
  disabled,
  children,
  ...props
}: BaseButtonRootProps) => {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      className={cn(rootVariants({ variant, size, tone, className }))}
      disabled={disabled || loading}
      {...props}
    >
      <Slot.Slottable>{children}</Slot.Slottable>
    </Comp>
  );
};

type SpanTagProps = ComponentProps<'span'>;
type BaseButtonContentProps = SpanTagProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  tone?: ButtonTone;
  loading?: boolean;
};

const contentVariants = cva(
  'group flex items-center justify-center truncate transition duration-100 ease-[cubic-bezier(0.3,0.7,0.4,1.5)]',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground text-sm font-bold group-hover:brightness-105',
        secondary: 'border-primary/90 text-primary/90 border-2 bg-white text-sm font-bold',
        ghost: 'text-foreground text-sm font-bold',
        destructive: 'bg-destructive text-sm font-bold text-white group-hover:brightness-105',
        underline: '',
        link: '',
      },
      tone: {
        destructive: '',
      },
      size: {
        md: 'h-10 rounded-md px-9 has-[>svg]:px-7',
        sm: 'h-8 rounded-sm px-7 has-[>svg]:px-5',
        xs: 'h-8 rounded-sm px-5 has-[>svg]:px-3',
        icon: 'size-10 rounded',
        'icon-sm': 'size-8 rounded',
        'icon-lg': 'size-12 rounded-sm',
        none: 'p-0',
      },
    },
    compoundVariants: [
      {
        variant: 'secondary',
        tone: 'destructive',
        class: 'border-destructive/90 text-destructive/90',
      },
      // Primary, secondary, and destructive get the 3D bounce effect
      {
        variant: ['primary', 'secondary', 'destructive'],
        size: 'md',
        class: '-translate-y-1.5 group-hover:-translate-y-2 group-active:-translate-y-0.5',
      },
      {
        variant: ['primary', 'secondary', 'destructive'],
        size: 'sm',
        class: '-translate-y-1 group-hover:-translate-y-1.5 group-active:-translate-y-0.5',
      },
      {
        variant: ['primary', 'secondary', 'destructive'],
        size: 'xs',
        class: '-translate-y-0.5 group-hover:-translate-y-1 group-active:-translate-y-0.5',
      },
      {
        variant: ['primary', 'secondary', 'destructive'],
        size: 'icon',
        class: '-translate-y-1.5 group-hover:-translate-y-2 group-active:-translate-y-0.5',
      },
      {
        variant: ['primary', 'secondary', 'destructive'],
        size: 'icon-sm',
        class: '-translate-y-1 group-hover:-translate-y-1.5 group-active:-translate-y-0.5',
      },
      {
        variant: ['primary', 'secondary', 'destructive'],
        size: 'icon-lg',
        class: '-translate-y-1.5 group-hover:-translate-y-2 group-active:-translate-y-0.5',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

const BaseButtonContent = ({
  className,
  children,
  loading,
  variant,
  size,
  tone,
  ...props
}: BaseButtonContentProps) => {
  return (
    <span className={cn(contentVariants({ variant, size, tone, className }))} {...props}>
      {loading && <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </span>
  );
};

export const BaseButton = { Root: BaseButtonRoot, Content: BaseButtonContent };
